import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { transformTranslate } from '@turf/turf';
import { GameRecord, PointOfInterest, convertDateTime } from 'diwimamodelweb/lib';
import { environment } from 'src/environments/environment';
import * as geojson from 'geojson';
import { BehaviorSubject } from 'rxjs';

export const byString = (o, s): any => {
  s = s.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
  s = s.replace(/^\./, '');           // strip a leading dot
  var a = s.split('.');
  for (var i = 0, n = a.length; i < n; ++i) {
    var k = a[i];
    if (!o) {
      return null
    } else {
      if (k in o) {
        o = o[k];
      } else {
        return null;
      }
    }
  }
  return o;
}

interface IExportMappings {
  eventInvitationExport: IExport[],
  gameRecordExport: IExport[],
  revierReportingExport: IExport[]
  geoObjectExport: IExport[]
}

interface IExport {
  attribute: string,
  resourceId: string,
  transform?: Function
  transformTranslate?: Function
}

export const ExportMappings: IExportMappings = {
  eventInvitationExport: [
    {
      attribute: "eventId",
      resourceId: "TABLE.eventInvitation.eventId"
    },
    {
      attribute: "eventName",
      resourceId: "TABLE.eventInvitation.eventName"
    },
    {
      attribute: "eventDate",
      resourceId: "TABLE.eventInvitation.eventDate"
    },
    {
      attribute: "huntingDistrictId",
      resourceId: "TABLE.eventInvitation.huntingDistrictId"
    },
    {
      attribute: "huntingDistrictName",
      resourceId: "TABLE.eventInvitation.huntingDistrictName"
    },
    {
      attribute: "personId",
      resourceId: "TABLE.eventInvitation.personId"
    },
    {
      attribute: "personAnrede",
      resourceId: "TABLE.eventInvitation.personAnrede"
    },
    {
      attribute: "personTitelAkademisch",
      resourceId: "TABLE.eventInvitation.personTitelAkademisch"
    },
    {
      attribute: "personTitelAndere",
      resourceId: "TABLE.eventInvitation.personTitelAndere"
    },
    {
      attribute: "personVorname",
      resourceId: "TABLE.eventInvitation.personVorname"
    },
    {
      attribute: "personNachname",
      resourceId: "TABLE.eventInvitation.personNachname"
    },
    {
      attribute: "personAnzeigeName",
      resourceId: "TABLE.eventInvitation.personAnzeigeName"
    },
    {
      attribute: "personGeschlecht",
      resourceId: "TABLE.eventInvitation.personGeschlecht"
    },
    {
      attribute: "personTelefon",
      resourceId: "TABLE.eventInvitation.personTelefon"
    },
    {
      attribute: "personEmailAddresse",
      resourceId: "TABLE.eventInvitation.personEmailAddresse"
    },
    {
      attribute: "creationDate",
      resourceId: "TABLE.eventInvitation.creationDate"
    },
    {
      attribute: "updateTimestamp",
      resourceId: "TABLE.eventInvitation.updateTimestamp"
    },
    {
      attribute: "processingStatus",
      resourceId: "TABLE.eventInvitation.processingStatus",
      transformTranslate: async (ref: string, translate: TranslateService): Promise<string> => {
        return translate.get(ref).toPromise()
      }
    },
    {
      attribute: "invitationExpiration",
      resourceId: "TABLE.eventInvitation.invitationExpiration"
    },
    {
      attribute: "invitationSend",
      resourceId: "TABLE.eventInvitation.invitationSend"
    },
    {
      attribute: "invitationViewed",
      resourceId: "TABLE.eventInvitation.invitationViewed"
    },
    {
      attribute: "invitationAccepted",
      resourceId: "TABLE.eventInvitation.invitationAccepted"
    },
    {
      attribute: "invitationDeclined",
      resourceId: "TABLE.eventInvitation.invitationDeclined"
    },
    {
      attribute: "personInvitationRef",
      resourceId: "TABLE.eventInvitation.personInvitationRef"
    },
    {
      attribute: "personDeleted",
      resourceId: "TABLE.eventInvitation.personDeleted"
    },
    {
      attribute: "personInvitationRef",
      resourceId: "TABLE.eventInvitation.url",
      transform: (ref: string): string => {
        return `${environment.invitationURL}/inv/${ref}`
      }
    },
  ],
  gameRecordExport: [
    {
      attribute: "timeStamp",
      resourceId: "FORM.gameRecordForm.timeStamp"
    },
    {
      attribute: "identifier",
      resourceId: "FORM.gameRecordForm.identifier"
    },
    {
      attribute: "tagid",
      resourceId: "FORM.gameRecordForm.tagid"
    },
    {
      attribute: "officialGameTag",
      resourceId: "FORM.gameRecordForm.officialGameTag"
    },
    {
      attribute: "wildart",
      resourceId: "FORM.gameRecordForm.wildart"
    },
    {
      attribute: "gameCount",
      resourceId: "FORM.gameRecordForm.gameCount"
    },
    {
      attribute: "gender",
      resourceId: "FORM.gameRecordForm.gender"
    },
    {
      attribute: "age",
      resourceId: "FORM.gameRecordForm.age"
    },
    {
      attribute: "quality",
      resourceId: "FORM.gameRecordForm.quality"
    },
    {
      attribute: "ageInYears",
      resourceId: "FORM.gameRecordForm.ageInYears"
    },
    {
      attribute: "statusTitle",
      resourceId: "FORM.gameRecordForm.statusTitle"
    },
    {
      attribute: "currentLocationTitle",
      resourceId: "FORM.gameRecordForm.currentLocationTitle"
    },
    {
      attribute: "erlegeArtTitle",
      resourceId: "FORM.gameRecordForm.erlegeArtTitle"
    },
    {
      attribute: "shotQuality",
      resourceId: "FORM.gameRecordForm.shotQuality"
    },
    {
      attribute: "meatQuality",
      resourceId: "FORM.gameRecordForm.meatQuality"
    },
    {
      attribute: "weights.dressedPreliminary",
      resourceId: "FORM.gameRecordForm.dressedPreliminary"
    },
    {
      attribute: "weights.salesAsWholeBody",
      resourceId: "FORM.gameRecordForm.salesAsWholeBody"
    },
    {
      attribute: "erlegerAnzeigeName",
      resourceId: "FORM.gameRecordForm.erlegerAnzeigeName"
    },
    {
      attribute: "erlegerId",
      resourceId: "FORM.gameRecordForm.erlegerId"
    },
    {
      attribute: "hasShootPrice",
      resourceId: "FORM.gameRecordForm.hasShootPrice"
    },
    {
      attribute: "shootPrice",
      resourceId: "FORM.gameRecordForm.shootPrice"
    },
    {
      attribute: "receiptNo",
      resourceId: "FORM.gameRecordForm.receiptNo"
    },
    {
      attribute: "postingAccount",
      resourceId: "FORM.gameRecordForm.postingAccount"
    },
    {
      attribute: "eventName",
      resourceId: "FORM.gameRecordForm.eventName"
    },
    {
      attribute: "eventId",
      resourceId: "FORM.gameRecordForm.eventId"
    },
    {
      attribute: "huntingDistrictName",
      resourceId: "FORM.gameRecordForm.huntingDistrictName"
    },
    {
      attribute: "huntingDistrictAdministrativeName",
      resourceId: "FORM.gameRecordForm.huntingDistrictAdministrativeName"
    },
    {
      attribute: "huntingDistrictId",
      resourceId: "FORM.gameRecordForm.huntingDistrictId"
    },
    {
      attribute: "plannedUtilizationDisplay",
      resourceId: "FORM.gameRecordForm.plannedUtilizationDisplay"
    },
    {
      attribute: "plannedUtilizationDetails.personName",
      resourceId: "FORM.gameRecordForm.plannedUtilizationDetails/personName"
    },
    {
      attribute: "plannedUtilizationDetails.personId",
      resourceId: "FORM.gameRecordForm.plannedUtilizationDetails/personId"
    },
    {
      attribute: "hunterLocation.name",
      resourceId: "FORM.gameRecordForm.hunterLocation"
    },
    {
      attribute: "hunterLocation.location.latitude",
      resourceId: "FORM.gameRecordForm.hunterLocationLat"
    },
    {
      attribute: "hunterLocation.location.longitude",
      resourceId: "FORM.gameRecordForm.hunterLocationLon"
    },
    {
      attribute: "placeOfKilling.name",
      resourceId: "FORM.gameRecordForm.placeOfKilling"
    },
    {
      attribute: "placeOfKilling.location.latitude",
      resourceId: "FORM.gameRecordForm.placeOfKillingLat"
    },
    {
      attribute: "placeOfKilling.location.longitude",
      resourceId: "FORM.gameRecordForm.placeOfKillingLon"
    },
    {
      attribute: "postMortemInspectionRequired",
      resourceId: "FORM.gameRecordForm.postMortemInspectionRequired"
    },
    {
      attribute: "postMortemInspectionComment",
      resourceId: "FORM.gameRecordForm.postMortemInspectionComment"
    },
    {
      attribute: "comment",
      resourceId: "FORM.gameRecordForm.comment"
    },
    {
      attribute:'pictureURLlinks',
      resourceId:"pictureURLlinks"
    }
  ],
  revierReportingExport: [
    
    {
      attribute: "timeStamp",
      resourceId: "FORM.gameRecordForm.killingDateTime"
    },
    {
      attribute: "identifier",
      resourceId: "FORM.gameRecordForm.identifier"
    },
    {
      attribute: "placeOfKillingLatitude",
      resourceId: "FORM.gameRecordForm.placeOfKillingLat"
    },
    {
      attribute: "placeOfKillingLongitude",
      resourceId: "FORM.gameRecordForm.placeOfKillingLon"
    },
    {
      attribute: "placeOfKillingLink",
      resourceId: "FORM.gameRecordForm.placeOfKillingMapLink"
    },
    {
      attribute: "tagid",
      resourceId: "FORM.gameRecordForm.diwimaTag"
    },
    {
      attribute: "officialGameTag",
      resourceId: "FORM.gameRecordForm.officialGameTag"
    },
    {
      attribute: "wildart",
      resourceId: "FORM.gameRecordForm.wildart"
    },
    {
      attribute: "gender",
      resourceId: "FORM.gameRecordForm.gender"
    },
    {
      attribute: "age",
      resourceId: "FORM.gameRecordForm.age"
    },
    {
      attribute: "ageInYears",
      resourceId: "FORM.gameRecordForm.ageInYears"
    },
    {
      attribute: "huntingDistrictName",
      resourceId: "FORM.gameRecordForm.huntingDistrictName"
    },
    {
      attribute: "huntingDistrictAdministrativeName",
      resourceId: "FORM.gameRecordForm.huntingDistrictAdministrativeName"
    },
    {
      attribute: "huntingDistrictId",
      resourceId: "FORM.gameRecordForm.huntingDistrictId"
    },
    {
      attribute: "erlegeArt",
      resourceId: "FORM.gameRecordForm.erlegeArt"
    },
    {
      attribute: "erlegeArtTitle",
      resourceId: "FORM.gameRecordForm.erlegeArtTitle"
    },
    {
      attribute: "pictureURLlinks",
      resourceId: "pictureURLlinks"
    }
    
  ],
  geoObjectExport: [
    {
      resourceId: "EXPORT.poiForm.identifier",
      attribute: "identifier",
    },
    {
      resourceId: "EXPORT.poiForm.yearOfConstruction",
      attribute:"yearOfConstruction",
    },
    {
      resourceId: "EXPORT.poiForm.lineWidth",
      attribute:"lineWidth",
    },
    {
      resourceId: "EXPORT.poiForm.huntingDistrictId",
      attribute:"huntingDistrictId",
    },
    {
      resourceId: "EXPORT.poiForm.huntingDistrictName",
      attribute:'huntingDistrictName',
    },
    {
      resourceId: "EXPORT.poiForm.type",
      attribute:'type',
    },
    {
      resourceId: "EXPORT.poiForm.typeTitle",
      attribute:'typeTitle',
    },
    {
      resourceId: "EXPORT.poiForm.tagid",
      attribute:'tagid',
    },
    {
      resourceId: "EXPORT.poiForm.maxPersonCount",
      attribute:'maxPersonCount',
    },
    {
      attribute:'commentUsage',
      resourceId: "EXPORT.poiForm.commentUsage",
    },
    {
      resourceId:"EXPORT.poiForm.borderColor",
      attribute:'borderColor',
    },
    {
      attribute:'createdByUserDisplayName',
      resourceId: "EXPORT.poiForm.createdByUser",
    },
    {
      resourceId: "EXPORT.poiForm.createdByUserId",
      attribute:'createdByUserId',
    },
    {
      attribute:'creationDate',
      resourceId: "EXPORT.poiForm.creationDate",
    },
    {
      resourceId: "EXPORT.poiForm.fillColor",
      attribute:'fillColor',
    },
    {
      resourceId: "EXPORT.poiForm.createdOnDeviceName",
      attribute:'createdOnDeviceName',
    },
    {
      resourceId: "EXPORT.poiForm.usageStatus",
      attribute:'usageStatus',
    },
    {
      resourceId: "EXPORT.poiForm.shortName",
      attribute:'shortName',
    },
    {
      resourceId: "EXPORT.poiForm.locationHeading",
      attribute:'locationHeading',
    },
    {
      resourceId: "EXPORT.poiForm.shortID",
      attribute:'shortID',
    },
    {
      resourceId: "EXPORT.poiForm.category",
      attribute:'category',
    },
    {
      resourceId: "EXPORT.poiForm.area",
      attribute:'area',
      transform: (area: unknown): number | string => {
        const m2 = Number(area);
        return area == null || area === '' || !Number.isFinite(m2) ? '' : Math.round(m2 / 100) / 100;
      },
    },
    {
      resourceId: "EXPORT.poiForm.length",
      attribute:'length',
    },
    {
      resourceId: "pictureURLlinks",
      attribute:'pictureURLlinks',
    },
    // {
    //   resourceId: "EXPORT.poiForm.coordinates",
    //   attribute:'encodedContent',
    // },
    // {
    //   resourceId: "EXPORT.poiForm.coordinates",
    //   attribute:'locationCoordinates',
    // }
  ]
}

@Injectable({
  providedIn: 'root'
})
export class ObjectExportService {
  geoJSONExportGeoObjects: BehaviorSubject<any> = new BehaviorSubject(undefined)

  constructor(
    private translate: TranslateService
  ) { }

  async toXlsxExport(arr: any[], paths: IExport[]): Promise<any[]> {
    const nList = []
    for (let record of arr) {
      const data = []
      // const data = {}
      for (let {attribute, resourceId, transform, transformTranslate} of paths) {
        const k = await this.translate.get(resourceId).toPromise()
        let v = byString(record, attribute)
        if (transform) {
          v = transform(v)
        }
        else if (transformTranslate) {
          v = await transformTranslate(v, this.translate)
        }
        data[k] = v
      }
      nList.push(data)
    }
    return nList
  }


  async prepareDatatoGeoJSONExport(data:any[], schema, link){
    let features = []
    switch(link){
      case 'geoObjects':{
        for (let object of data) {
          var geometry : geojson.Geometry
          var properties:geojson.GeoJsonProperties = {}

          await schema.forEach(field => {
            if(object[field['key']]){
              if(field['key'] == "creationDate"){
                properties[field['name']] = convertDateTime(object[field['key']])
              }else
              properties[field['name']] = object[field['key']]
            }
            else
            properties[field['name']] = null
          });

          if(object.encodedContent){
            const encodedContent = object.encodedContent.split(" ")
            let pairs
            pairs = encodedContent.reduce(function(result, value, indexV, array) {
              if (indexV % 2 === 0){
                const splited = array.slice(indexV, indexV + 2)
                var lat: number = +splited[1];
                var lng: number = +splited[0];
                const value =[ lat, lng ]
                result.push(value);
              }
              return result;
            }, []);

            if(object?.contentType == 'polygon'){
              geometry = {
                type: 'MultiPolygon',
                coordinates: [[pairs]]
              };
            }

            if(object?.contentType == 'line'){
              geometry = {
                type: 'MultiLineString',
                coordinates: [pairs]
              };
            }
          }

          if(object.locationCoordinates){
            geometry = {
              type: 'Point',
              coordinates: [object.locationCoordinates.longitude, object.locationCoordinates.latitude]
            };
          }
    
          var feature: geojson.Feature = {
            type: "Feature",
            geometry: geometry,
            id:object['identifier'],
            properties:properties,
          }
          features.push(feature)
        }
      }
    }
    

    var geoJsonFeatures: geojson.FeatureCollection = {
      type: 'FeatureCollection',
      features: features
    }
    this.geoJSONExportGeoObjects.next(geoJsonFeatures)

    return geoJsonFeatures
  }

  toGeoJSONExport(data, filename){
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/geo+json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.geojson`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
